import React, { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import config from 'config'
import { ProfileImage } from 'components/styled/avatar/default-profile-image'
import { StyledTheme } from 'theme'
import { ThemeColor } from 'theme/colors'

interface AvatarProps {
  filename?: string
  isRound?: boolean
  showBorder?: boolean
  size?: keyof StyledTheme['size']['profileImage']
  bgColor?: ThemeColor
}

const AvatarContainer = styled.div`
  position: relative;
`

const StyledAvatar = styled.div<{
  size: keyof StyledTheme['size']['profileImage']
  $isRound: boolean
  $showBorder: boolean
}>`
  position: relative;

  width: ${({ theme, size }) => theme.size.profileImage[size]};
  max-width: 100%;
  line-height: 0;
  flex-shrink: 0;

  overflow: hidden;
  border: ${({ theme, $showBorder }) =>
    $showBorder ? `${theme.space.s2} solid ${theme.color.primary110}` : 'none'};
  border-radius: ${({ $isRound }) => ($isRound ? '50%' : '0%')};
`

const PulsingCircle = styled.div<{
  size: keyof StyledTheme['size']['profileImage']
}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  animation: ${({ theme }) => theme.animation.pulse} 2s infinite;
`

const StyledImage = styled.div<{
  size: keyof StyledTheme['size']['profileImage']
  $url: string
}>`
  width: ${({ theme }) => theme.size.profileImage.large};
  max-width: 100%;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${({ $url }) => $url});
  background-size: cover;
  background-position: 50% 50%;
`

export const Avatar = ({
  filename,
  size = 'medium',
  isRound = true,
  showBorder = false,
  bgColor,
  ...props
}: AvatarProps) => {
  const [pictureExists, setPictureExists] = useState<boolean | undefined>(
    undefined
  )

  useEffect(() => {
    if (filename && filename.startsWith('http')) {
      fetch(filename, { method: 'get' }).then(response => {
        if (response.status === 200) {
          setPictureExists(true)
        } else {
          setPictureExists(false)
        }
      })
    }
  }, [])

  let url: string | undefined

  if (filename && !filename.startsWith('http')) {
    url = `${config.USER_UPLOADS_BASE_URL}/b2c-screenshots/${filename}.jpg`
  } else if (pictureExists) {
    url = filename
  } else {
    url = undefined
  }

  return (
    <AvatarContainer>
      {showBorder && <PulsingCircle size={size} />}
      <StyledAvatar
        $showBorder={showBorder}
        $isRound={isRound}
        size={size}
        {...props}
      >
        <ProfileImage isRound={isRound} bgColor={bgColor} />
        {url && <StyledImage size={size} $url={url} />}
      </StyledAvatar>
    </AvatarContainer>
  )
}
